// PassportPermissions.js
import React, { useState, useEffect, createContext, useContext } from 'react';

const PermissionsContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const [userPermissions, setUserPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserPermissions();
  }, []);

  const loadUserPermissions = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const permissions = JSON.parse(user.Permissions || '{}');
      
      // إضافة صلاحيات افتراضية مؤقتاً للاختبار
      const passportPermissions = permissions["إدارة الجوازات"] || {};
      
      // منح جميع الصلاحيات مؤقتاً للمدير
      if (user.Username === 'admin' || Object.keys(passportPermissions).length === 0) {
        const allPermissions = {
          "عرض الجوازات": true,
          "إضافة جواز جديد": true, 
          "تعديل الجوازات": true,
          "حذف الجوازات": true,
          "الإدخال السريع": true,
          "المعالجة المجمعة": true,
          "ضبط النموذج": true,
          "إنشاء سفرة جديدة": true,
          "إدارة الملاحظات": true,
          "تسليم الجوازات": true,
          "تراجع عن التسليم": true
        };
        setUserPermissions(allPermissions);
      } else {
        setUserPermissions(passportPermissions);
      }
    } catch (error) {
      console.error('خطأ في تحميل الصلاحيات:', error);
      // في حالة الخطأ، امنح صلاحيات العرض على الأقل
      setUserPermissions({"عرض الجوازات": true});
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (permission) => {
    return userPermissions[permission] === true;
  };

  const hasAnyPermission = (permissions) => {
    return permissions.some(permission => hasPermission(permission));
  };

  const canViewOnly = () => {
    return hasPermission("عرض فقط") && 
           !hasPermission("تعديل الجوازات") && 
           !hasPermission("إضافة جواز جديد");
  };

  const canDeliver = () => {
    return hasPermission("تسليم الجوازات");
  };

  const canRevert = () => {
    return hasPermission("تراجع عن التسليم");
  };

  return (
    <PermissionsContext.Provider value={{
      userPermissions,
      loading,
      hasPermission,
      hasAnyPermission,
      canViewOnly,
      canDeliver,
      canRevert,
      refreshPermissions: loadUserPermissions
    }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const PermissionGate = ({ 
  permissions, 
  children 
}) => {
  const { hasPermission, hasAnyPermission } = usePermissions();

  if (typeof permissions === 'string') {
    if (!hasPermission(permissions)) {
      return null;
    }
  } else if (Array.isArray(permissions)) {
    if (!hasAnyPermission(permissions)) {
      return null;
    }
  }

  return children;
};